import { useContext, useEffect, useState } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  format,
  parseISO,
  differenceInYears,
  addMinutes,
  isWithinInterval,
} from 'date-fns'
// switch to this when calendar ids are fixed
import { apiClient } from 'utils/api/apiClient'

import Box from '@mui/material/Box'
import { Button, Select, Grid, IconButton } from '@mui/material'
// import Grid2 from '@mui/material/Grid2'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import Typography from '@mui/material/Typography'

import google from 'assets/images/logos/google_meets.png'
import teams from 'assets/images/logos/ms_teams.png'
import zoom from 'assets/images/logos/zoom.png'

// Soft UI Dashboard PRO React components
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import OnboardingMultiOptionTile from '../OnboardingMultiOptionTile'
import { selectData } from 'layouts/onboarding'
import { callZapier } from 'utils/zapier/callZapier'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowBack,
  CheckCircle,
  Close,
  East,
  Flag,
  Toll,
} from '@mui/icons-material'
import deskChair from 'assets/images/deskandchair.svg'
import Banner from 'atoms/Banner'
import gapGif from 'assets/images/gapAnimation.gif'
import GapLoadingPage from './components/GapLoadingPage'

export const AnnuityRecommender = ({
  closeModal = () => console.log('Schedule Appointment'),
  isAdmin = false,
  isOnPageForm = false,
  parcoReadinessScore4Percent = 0,
  adjustedNetTakeHomeDifferencePARCOGap = 0,
  valueOfInvestableAssets = 0,
  parcoAnnuityRecommendation = {},
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(false)
  const [data, setData] = useState(null)
  const [availableDates, setAvailableDates] = useState(null)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedPlatform, setSelectedPlatform] = useState(null)
  const [assistedLiving, setAssistedLiving] = useState(null)
  const [inflation, setInflation] = useState(null)
  const [gapSelection, setGapSelection] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [availableTimes, setAvailableTimes] = useState([])

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  // Pagination
  const [open, setOpen] = useState(true)
  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState(1)
  const handleNext = () => {
    setDirection(1)
    setPage((prev) => Math.min(prev + 1, pages.length - 1))
  }
  const handleBack = () => {
    setDirection(-1)
    setPage((prev) => Math.max(prev - 1, 0))
  }
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const dateToYears = (startDate, endDate) => {
    return startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  }

  // Eastern Time Formatter
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  const fetchAvailability = async () => {
    const now = new Date()
    const begin =
      now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate()
    const end =
      now.getFullYear() + '-' + (now.getMonth() + 3) + '-' + now.getDate()
    try {
      const calendarIds = []

      // IF HAS PRIMARY STRATEGIST
      if (
        intakeState.retirementStrategist !== '' &&
        intakeState.retirementStrategist
      ) {
        const strategistMatch = selectData.retirementStrategist.find(
          (s) =>
            s.value.toLowerCase().trim() ===
            intakeState.retirementStrategist.toLowerCase().trim()
        )

        if (strategistMatch) {
          // console.log('Times available for ' + strategistMatch.value)
          calendarIds.push(strategistMatch.id)
        } else {
          console.warn(
            'Strategist not found in selectData:',
            intakeState.retirementStrategist
          )
        }
      } else {
        if (dateToYears(intakeState?.dateOfBirth, new Date()) > 59) {
          //quinn
          // console.log('times avaiable for quinn')

          calendarIds.push(selectData.retirementStrategist[2].id)
        } else if (
          intakeState.agency ===
            'u.s._agency_for_international_development_(usaid)' ||
          intakeState.agency === 'department_of_state_(dos)'
        ) {
          //dan
          // console.log('times avaiable for dan')
          calendarIds.push(selectData.retirementStrategist[1].id)
        } else {
          //dan & john
          // console.log('times avaiable for dan & john')

          calendarIds.push(selectData.retirementStrategist[0].id)
          calendarIds.push(selectData.retirementStrategist[1].id)
        }
      }

      const payload = {
        begin: begin,
        end: end,
        calendar_ids_to_check: calendarIds.join('%2C'),
      }

      const response = await fetch(
        'https://1p9cjflrkb.execute-api.us-east-2.amazonaws.com/api/users/availability',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )

      const data = await response.json()

      // const { data } = await apiClient.post('/api/users/availability', {
      //   body: payload,
      // })
      setData(data)

      const availableDatesSet = new Set(
        data.availability.map((iso) => iso.split('T')[0])
      )
      setAvailableDates(availableDatesSet)
    } catch (e) {
      console.error('Error fetching availability:', e)
    }
  }

  // console.log(data.availability)

  // Initial fetch of availability data
  useEffect(() => {
    fetchAvailability()
  }, [])

  useEffect(() => {
    setSelectedTime(null) // Reset the selected time when date changes
  }, [selectedDate])

  const getAvailableTimes = (selectedDate) => {
    if (!data?.availability || !selectedDate) return []

    return data.availability
      .filter((isoString) => {
        const datePart = isoString.split('T')[0]
        return datePart === selectedDate
      })
      .map((isoString) => {
        const time = formatter.format(new Date(isoString))
        return { time: time, timeISO: isoString }
      })
  }

  // Update available times when the selected date changes
  useEffect(() => {
    if (!selectedDate || !data) return
    setAvailableTimes(getAvailableTimes(selectedDate))
  }, [selectedDate, data])

  const scheduleWith = intakeState?.retirementStrategist
    ? selectData.retirementStrategist.find(
        (s) => s.value === intakeState.retirementStrategist
      )?.sfId
    : dateToYears(intakeState?.dateOfBirth, new Date()) > 59
    ? selectData.retirementStrategist[3].sfId +
      ', ' +
      selectData.retirementStrategist[2].sfId
    : intakeState?.agency ===
        'u.s._agency_for_international_development_(usaid)' ||
      intakeState?.agency === 'department_of_state_(dos)'
    ? selectData.retirementStrategist[1].sfId
    : selectData.retirementStrategist[0].sfId +
      ', ' +
      selectData.retirementStrategist[1].sfId

  const clientAge = intakeState?.dateOfBirth
    ? differenceInYears(new Date(), new Date(intakeState?.dateOfBirth))
    : 'Date of Birth Not Provided'

  const sendApptToSchedule = () => {
    const eventToSchedule = {
      firstName: intakeState?.firstName || 'No First Name',
      lastName: intakeState?.lastName || 'No Last Name',
      email: intakeState?.email || 'No Email',
      phone: intakeState?.phone || 'No Phone',
      leadId: intakeState?.salesforceLeadID || 'No Lead ID',
      accountId: intakeState?.salesforceAccountID || 'No Account ID',
      scheduleWith: scheduleWith,
      selectedDate: format(new Date(selectedTime), 'MM/dd/yyyy') || '',
      selectedTime: formatter.format(new Date(selectedTime)) + ' EST' || '',
      dateTimeISO: selectedTime || '',
      selectedPlatform: selectedPlatform || '',
      meetingTypeWithduration:
        selectData.appointmentTypes.find(
          ({ value }) => value === selectedType
        ) || '',
      assistedLiving: assistedLiving,
      inflation: inflation,
      gapSelection: gapSelection,
      parcoReadinessScore4Percent: isNaN(parcoReadinessScore4Percent)
        ? 0
        : parcoReadinessScore4Percent,
      adjustedNetTakeHomeDifferencePARCOGap: isNaN(
        adjustedNetTakeHomeDifferencePARCOGap * 12
      )
        ? 0
        : adjustedNetTakeHomeDifferencePARCOGap * 12,
      investableAssets: isNaN(valueOfInvestableAssets)
        ? 0
        : valueOfInvestableAssets,
      age: clientAge,
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/27b0m1u/',
      payload: {
        eventToSchedule: eventToSchedule,
      },
      notificationMessage: `Your Appointment on ${format(
        new Date(selectedTime),
        'MM/dd/yyyy'
      )} at ${formatter.format(
        new Date(selectedTime)
      )} EST is being scheduled!`,
      sendNotification: false,
    })
    setAppointmentConfirmed(true)
    updateIntakeStateAtKey({ appointmentScheduled: true })
    handleNext()
    // closeModal()
  }

  const pages = [
    <Grid key="0" item xs={12} mb={3} sx={{}}>
      <Box>
        <Typography variant="headerTitle" sx={{ color: '#262627' }}>
          What is my Retirement Gap?
        </Typography>
      </Box>
      <Typography variant="body1" mt={{ xs: 2, md: 3 }}>
        Your Retirement Income Gap is the difference between your current income
        and your income in retirement. To maintain your current lifestyle, those
        incomes should match and your gap should be close to $0.
      </Typography>
      <Typography variant="body1" mt={2}>
        Let's explore options for bridging your gap!
      </Typography>
      <Box display="flex" justifyContent="space-between" mt={3} sx={{}}>
        <Banner
          color={adjustedNetTakeHomeDifferencePARCOGap < 0 ? 'green' : 'red'}
          arrow={false}
          title={
            adjustedNetTakeHomeDifferencePARCOGap < 0
              ? 'CURRENT RETIREMENT SURPLUS'
              : 'CURRENT RETIREMENT GAP'
          }
          description={dollarFormat.format(
            Math.abs(adjustedNetTakeHomeDifferencePARCOGap)
          )}
          subText="/month"
        />
        <Box
          sx={{
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <East
            sx={{
              color: '#262627',
              height: '30px',
              width: '30px',
            }}
          />
        </Box>
        <Banner
          color={'green'}
          arrow={false}
          title={'NEW RETIREMENT GAP'}
          description={dollarFormat.format(0)}
          subText="/month"
        />
      </Box>
    </Grid>,
    <Grid key="1" item xs={12} mb={3} sx={{}}>
      <Box>
        <Typography
          variant="headerTitle"
          sx={{ color: '#262627', fontSize: { xs: '24px', sm: '32px' } }}
        >
          How concerned are you about living in an Assisted Living Facility?
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Please answer as honestly as possible for the best results:
      </Typography>
      <Box sx={{}}>
        <OnboardingMultiOptionTile
          header={' '}
          helpText={''}
          subHeader={' '}
          expectedDataType={'string'}
          tileSize={{ xs: '4' }}
          onChange={(e) => {
            setAssistedLiving(e)
          }}
          onTileClick={handleNext}
          optionArray={[
            {
              optionLabel: 'Not Concerned',
              optionValue: 'notConcernedAssistedLiving',
            },
            {
              optionLabel: `Hadn't thought about it`,
              optionValue: 'hadntThoughtAssistedLiving',
            },
            {
              optionLabel: 'Concerned',
              optionValue: 'concernedAssistedLiving',
            },
          ]}
        />
      </Box>
    </Grid>,
    <Grid key="2" item xs={12} mb={3} sx={{}}>
      <Box>
        <Typography
          variant="headerTitle"
          sx={{ color: '#262627', fontSize: { xs: '24px', sm: '32px' } }}
        >
          How concerned are you about inflation?
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Please answer as honestly as possible for the best results:
      </Typography>
      <Box sx={{}}>
        <OnboardingMultiOptionTile
          id={'inflation'}
          header={' '}
          helpText={''}
          subHeader={' '}
          expectedDataType={'string'}
          tileSize={{ xs: '4' }}
          onChange={(e) => {
            setInflation(e)
          }}
          onTileClick={handleNext}
          optionArray={[
            {
              optionLabel: 'Not Concerned',
              optionValue: 'notConcernedInflation',
            },
            {
              optionLabel: `Hadn't thought about it`,
              optionValue: 'hadntThoughtInflation',
            },
            {
              optionLabel: 'Concerned',
              optionValue: 'concernedInflation',
            },
          ]}
        />
      </Box>
    </Grid>,
    <GapLoadingPage
      key="3"
      handleNext={handleNext}
      adjustedNetTakeHomeDifferencePARCOGap={
        adjustedNetTakeHomeDifferencePARCOGap
      }
    />,
    <Grid key="4" item xs={12} mb={3} sx={{}}>
      <Box>
        <Typography
          variant="headerTitle"
          sx={{ color: '#262627', fontSize: { xs: '24px', sm: '32px' } }}
        >
          Choose how you'd prefer to bridge your Income Gap in retirement
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Both options improve your financial situation in retirement.
      </Typography>
      <Box sx={{}}>
        <OnboardingMultiOptionTile
          header={' '}
          helpText={''}
          subHeader={' '}
          expectedDataType={'string'}
          tileSize={{ xs: '12' }}
          onChange={(e) => {
            setGapSelection(e)
          }}
          onTileClick={handleNext}
          optionArray={[
            {
              className: 'textWithDescription',
              optionLabel: `Re-Allocate ${
                dateToYears(intakeState?.dateOfBirth, new Date()) < 50
                  ? 'Savings'
                  : parcoAnnuityRecommendation?.cost
                  ? dollarFormat.format(parcoAnnuityRecommendation.cost)
                  : 'Savings'
              } with a Different Strategy`,
              optionLabelBadge: 'Recommended',
              optionDescription: `Close your income gap and receive guaranteed income in retirement for the rest of your life.`,
              optionTimer: 'Lifetime Income Guaranteed',
              optionTimerIcon: (
                <CheckCircle
                  sx={{
                    marginBottom: '-2px',
                    marginRight: '5px',
                  }}
                />
              ),
              optionValue: 'annuity',
            },
            {
              className: 'textWithDescription',
              optionLabel: `Use ${
                parcoAnnuityRecommendation.threePercent
                  ? parcoAnnuityRecommendation.roundedCurrentAge >= 70
                    ? `${dollarFormat.format(
                        parcoAnnuityRecommendation.fourPercent
                      )} of`
                    : dateToYears(intakeState?.dateOfBirth, new Date()) < 50
                    ? `the 3% rule with`
                    : `${dollarFormat.format(
                        parcoAnnuityRecommendation.threePercent
                      )} of`
                  : ''
              } your Retirement Savings`,
              optionDescription: `Based on the assumption that you will be spending ${
                parcoAnnuityRecommendation.roundedCurrentAge >= 70 ? '4%' : '3%'
              } of your retirement savings each year in retirement to close your income gap.`,
              optionTimer: 'Withdrawals estimated to last 30 years',
              // optionTimerIcon: (
              //   <img
              //     style={{
              //       width: '20px',
              //       height: '20px',
              //       marginRight: '6px',
              //     }}
              //     src="https://upload.wikimedia.org/wikipedia/commons/b/b6/US-ThriftSavingsPlan-Logo.svg"
              //     alt=""
              //   />
              // ),
              optionValue: 'tspPercentRule',
            },
          ]}
        />
      </Box>
    </Grid>,
    <Grid
      key="5"
      item
      xs={12}
      mb={5}
      sx={{
        '& .MuiDayCalendar-weekDayLabel': {
          margin: '0px 0.5vw',
        },
        '& .MuiPickersDay-root': {
          fontWeight: 600,
          margin: '0px 0.5vw',
        },
        '& .MuiPickersDay-root.Mui-disabled': {
          fontWeight: 400,
        },
        '& .MuiPickersDay-root.Mui-selected': {
          backgroundColor: '#0671AD',
        },
        '& .MuiPickersDay-root.Mui-selected:active, & .MuiPickersDay-root.Mui-selected:focus, & .MuiPickersDay-root.Mui-selected:hover':
          {
            backgroundColor: '#045a8a',
          },
        '& .MuiPickersDay-root:hover': {
          backgroundColor: '#e6f0f6',
        },
        '& .MuiPickersCalendarHeader-labelContainer': {
          position: 'absolute',
          left: '50%',
          transform: 'translate(-50%)',
        },
        '& .MuiPickersArrowSwitcher-root': {
          justifyContent: 'space-between',
          width: '100%',
        },
      }}
    >
      <Box>
        <Typography variant="headerTitle" sx={{ color: '#262627' }}>
          Ensure this option is right for you, meet with a PARCO team member!
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Select what day would be best for us to chat:
      </Typography>
      <DateCalendar
        sx={{ width: '100%' }}
        shouldDisableDate={(date) => {
          const today = new Date()
          today.setHours(0, 0, 0, 0) // Normalize today's date (remove time part)

          const twoMonthsLater = new Date()
          twoMonthsLater.setMonth(today.getMonth() + 2)

          const dateString = format(date, 'yyyy-MM-dd')

          return (
            date < today ||
            date > twoMonthsLater ||
            (availableDates && !availableDates.has(dateString))
          ) // Disable less than today, older than 2 months and dates with no availability
        }}
        value={selectedDate ? parseISO(selectedDate) : null}
        onChange={(newValue) => {
          setSelectedDate(format(newValue, 'yyyy-MM-dd'))

          setSelectedTime(null)
        }}
        views={['day']}
      />
      {selectedDate && (
        <>
          <Typography variant="body1" mb={1}>
            Select a time from the times available:
          </Typography>
          {renderMuiFormField({
            key: selectedDate,
            autoFocus: false,
            label: '',
            placeholder: 'Select a Meeting Timeslot (Eastern Time)',
            inputType: 'select',
            value: selectedTime,
            onChange: (time) => setSelectedTime(time),
            options: availableTimes.map((time) => ({
              value: time.timeISO,
              label: time.time + ' EST',
            })),
          })}
        </>
      )}
    </Grid>,
    <Grid key="6" item xs={12}>
      <Box mb={1}>
        <Typography variant="headerTitle" sx={{ color: '#262627' }}>
          Which platform would you prefer to meet with?
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Please select one of the following:
      </Typography>
      <Box>
        <OnboardingMultiOptionTile
          header={' '}
          helpText={''}
          subHeader={' '}
          expectedDataType={'string'}
          tileSize={{ xs: '4' }}
          onChange={(e) => {
            setSelectedPlatform(e)
          }}
          optionArray={[
            {
              optionLabel: 'Google Meet',
              optionValue: 'google_meet_retirement_consultation',
              optionIcon: <img src={google}></img>,
            },
            {
              optionLabel: 'Zoom',
              optionValue: 'zoom_retirement_consultation',
              optionIcon: <img src={zoom}></img>,
            },
            {
              optionLabel: 'Microsoft Teams',
              optionValue: 'ms_teams_retirement_consultation',
              optionIcon: <img src={teams}></img>,
            },
          ]}
        />
        <Box display="flex" justifyContent="flex-start">
          <Button
            variant="contained"
            disabled={!selectedDate || !selectedTime || !selectedPlatform}
            onClick={sendApptToSchedule}
            sx={{
              padding: '8px 24px',
              backgroundColor: '#2370ED',
              border: '2px solid #2370ED',
              color: '#fff',
              boxShadow: 'none',
              borderRadius: '100px',
              textTransform: 'capitalize',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: 1.5,
              '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                transform: 'none',
                backgroundColor: '#1c59bd',
                borderColor: '#1c59bd',
                boxShadow: 'none',
              },
              '&:disabled': {
                borderColor: 'rgba(0,0,0,0)',
              },
            }}
          >
            Schedule Meeting
          </Button>
        </Box>
      </Box>
    </Grid>,
    <Grid key="7" item xs={12}>
      <Box>
        <Typography variant="headerTitle" sx={{ color: '#262627' }}>
          Meeting scheduled for {format(new Date(selectedTime), 'MM/dd/yyyy')}{' '}
          at {formatter.format(new Date(selectedTime))} EST
        </Typography>
        <Typography variant="body1" mt={3}>
          Your meeting details will be emailed to you shortly.
        </Typography>
        <img
          src={deskChair}
          style={{
            maxWidth: '400px',
            display: 'block',
            margin: '32px auto',
          }}
        />
      </Box>
    </Grid>,
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          justifyContent: 'space-between',
          position: 'relative',
          display: `${!isOnPageForm ? 'flex' : 'none'}`,
          top: { xs: -8, sm: -64 },
        }}
      >
        {page > 0 && (
          <IconButton
            onClick={handleBack}
            sx={{
              position: 'absolute',
              top: 0,
              left: { xs: -8, sm: -16 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        )}

        <IconButton
          onClick={closeModal}
          sx={{
            position: 'absolute',
            top: 0,
            right: { xs: -8, sm: -16 },
            color: '#fff',
            zIndex: 1,
            backgroundColor: '#000',
            '&:hover': {
              backgroundColor: '#000',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Grid
        sx={{ textAlign: 'left', overflow: 'hidden' }}
        px={{ xs: 0, sm: 4 }}
        pt={4}
        pb={{ xs: 2, sm: 4 }}
      >
        {/* <motion.div
          animate={{ height }}
          transition={{ duration: 0.3 }}
          style={{ overflow: 'hidden' }}
        > */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            style={{ position: 'relative', width: '100%' }}
          >
            {pages[page]}
            <Box
              display="flex"
              justifyContent={page === 0 ? 'flex-end' : 'flex-start'}
            >
              {page === 0 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  // disabled={!selectedDate}
                  sx={{
                    padding: '8px 24px',
                    backgroundColor: '#2370ED',
                    border: '2px solid #2370ED',
                    color: '#fff',
                    boxShadow: 'none',
                    borderRadius: '100px',
                    textTransform: 'capitalize',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: 1.5,
                    '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                      transform: 'none',
                      backgroundColor: '#1c59bd',
                      borderColor: '#1c59bd',
                      boxShadow: 'none',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(0,0,0,0)',
                    },
                  }}
                >
                  Explore My Options
                </Button>
              ) : page === 5 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!selectedDate || !selectedTime}
                  sx={{
                    padding: '8px 24px',
                    backgroundColor: '#2370ED',
                    border: '2px solid #2370ED',
                    color: '#fff',
                    boxShadow: 'none',
                    borderRadius: '100px',
                    textTransform: 'capitalize',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: 1.5,
                    '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                      transform: 'none',
                      backgroundColor: '#1c59bd',
                      borderColor: '#1c59bd',
                      boxShadow: 'none',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(0,0,0,0)',
                    },
                  }}
                >
                  Continue to Select Platform
                </Button>
              ) : page === 6 ? (
                <></>
              ) : page < pages.length - 1 ? (
                <></>
              ) : (
                // <Button
                //   variant="contained"
                //   onClick={handleNext}
                //   // disabled={!selectedDate}
                //   sx={{
                //     fontSize: '15px',
                //     padding: '12px 24px',
                //     backgroundColor: '#2370ED',
                //     color: '#fff',
                //     fontWeight: 'normal',
                //     boxShadow: 'none',
                //     textTransform: 'capitalize',
                //     letterSpacing: 0.5,
                //     borderRadius: '100px',
                //     '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                //       transform: 'none',
                //       backgroundColor: '#1c59bd',
                //       boxShadow: 'none',
                //     },
                //   }}
                // >
                //   Next
                // </Button>
                <>
                  {!isOnPageForm && (
                    <Button
                      variant="contained"
                      onClick={closeModal}
                      sx={{
                        padding: '8px 24px',
                        backgroundColor: '#2370ED',
                        border: '2px solid #2370ED',
                        color: '#fff',
                        boxShadow: 'none',
                        borderRadius: '100px',
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: 1.5,
                        '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                          transform: 'none',
                          backgroundColor: '#1c59bd',
                          borderColor: '#1c59bd',
                          boxShadow: 'none',
                        },
                        '&:disabled': {
                          borderColor: 'rgba(0,0,0,0)',
                        },
                      }}
                    >
                      Close
                    </Button>
                  )}
                </>
              )}
            </Box>
          </motion.div>
        </AnimatePresence>
        {/* </motion.div> */}
      </Grid>
    </LocalizationProvider>
  )
}
